import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  UserPlus,
  X,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  FileSpreadsheet,
  Calendar,
  BarChart2,
  TrendingUp,
  Target,
  CheckCircle,
} from "lucide-react";
import ExcelJS from "exceljs";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";

// ─── API Helper ──────────────────────────────────────────────────────────────
// ملاحظة: عدّل BASE_URL لو عندك ملف api.js / axiosInstance خاص بالمشروع،
// واستبدل الدوال دي بنداءاته. هنا استخدمت fetch + token من localStorage
// لحد ما تبعت الملف الفعلي المستخدم في باقي الصفحات.
const BASE_URL = process.env.REACT_APP_API_URL || "/api";

async function apiRequest(path, options = {}) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    cache: "no-store", // مهم: يمنع رجوع 304 Not Modified اللي fetch بيعتبره فشل
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(
      data.message || `حدث خطأ غير متوقع (status: ${res.status})`,
    );
  return data;
}

const PlanAPI = {
  getPeriods: () => apiRequest("/plan/periods"),
  createPeriod: (body) =>
    apiRequest("/plan/periods", { method: "POST", body: JSON.stringify(body) }),
  getActivities: (periodId) =>
    apiRequest(`/plan/periods/${periodId}/activities`),
  createActivity: (body) =>
    apiRequest("/plan/activities", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateActivityCell: (id, columnName, value) =>
    apiRequest(`/plan/activities/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ columnName, value }),
    }),
  deleteActivity: (id) =>
    apiRequest(`/plan/activities/${id}`, { method: "DELETE" }),
};

// تحويل الصف "المسطح" القادم من الـ API (parentActivityId) إلى شكل شجري
// { ...activity, subTasks: [...] } كما تتوقعه الواجهة
function buildActivityTree(flatRows) {
  const mainRows = flatRows.filter((r) => !r.parentActivityId);
  const subRows = flatRows.filter((r) => r.parentActivityId);
  return mainRows.map((m) => ({
    ...m,
    planned: m.plannedVolume,
    actual: m.actualVolume,
    q: m.criteriaQ,
    s: m.criteriaS,
    subTasks: subRows
      .filter((s) => s.parentActivityId === m.id)
      .map((s) => ({
        ...s,
        planned: s.plannedVolume,
        actual: s.actualVolume,
        q: s.criteriaQ,
        s: s.criteriaS,
      })),
  }));
}

// ─── Floating Label Input ────────────────────────────────────────────────────
function FloatingInput({
  id,
  label,
  type = "text",
  value,
  onChange,
  required,
  className = "",
}) {
  const [focused, setFocused] = useState(false);
  const active =
    focused || (value !== "" && value !== undefined && value !== null);
  return (
    <div className={`relative ${className}`}>
      <input
        id={id}
        type={type}
        value={value ?? ""}
        onChange={(e) =>
          onChange(
            type === "number"
              ? e.target.value === ""
                ? ""
                : Number(e.target.value)
              : e.target.value,
          )
        }
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        required={required}
        className={`
          peer w-full border rounded-xl px-4 pt-5 pb-2 text-sm text-right
          bg-white outline-none transition-all duration-200
          ${focused ? "border-[#546E7A] shadow-[0_0_0_3px_rgba(84,110,122,0.12)]" : "border-gray-200"}
          ${required && !value ? "focus:border-[#546E7A]" : ""}
        `}
        dir="rtl"
      />
      <label
        htmlFor={id}
        className={`
          absolute right-4 transition-all duration-200 pointer-events-none origin-right
          ${
            active
              ? "top-1.5 text-[10px] font-semibold text-[#546E7A]"
              : "top-3.5 text-sm text-gray-400"
          }
        `}
      >
        {label}
        {required && <span className="text-red-400 mr-0.5">*</span>}
      </label>
    </div>
  );
}

// ─── Floating Label Select ───────────────────────────────────────────────────
function FloatingSelect({
  id,
  label,
  value,
  onChange,
  options,
  className = "",
}) {
  const [focused, setFocused] = useState(false);
  const active = focused || !!value;
  return (
    <div className={`relative ${className}`}>
      <select
        id={id}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        dir="rtl"
        className={`
          w-full border rounded-xl px-4 pt-5 pb-2 text-sm text-right bg-white
          outline-none transition-all duration-200 appearance-none cursor-pointer
          ${focused ? "border-[#546E7A] shadow-[0_0_0_3px_rgba(84,110,122,0.12)]" : "border-gray-200"}
        `}
      >
        <option value="" disabled></option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <label
        htmlFor={id}
        className={`
          absolute right-4 transition-all duration-200 pointer-events-none origin-right
          ${
            active
              ? "top-1.5 text-[10px] font-semibold text-[#546E7A]"
              : "top-3.5 text-sm text-gray-400"
          }
        `}
      >
        {label}
      </label>
      <ChevronDown
        size={14}
        className="absolute left-3 top-4 text-gray-400 pointer-events-none"
      />
    </div>
  );
}

// ─── Inline Editable Cell ────────────────────────────────────────────────────
function EditableCell({ value, onSave, type = "text", className = "" }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef(null);

  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      ref.current.select?.();
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== value) onSave(type === "number" ? Number(draft) : draft);
  };

  if (editing) {
    return (
      <input
        ref={ref}
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setDraft(value);
            setEditing(false);
          }
        }}
        className={`w-full text-center border-b-2 border-[#546E7A] bg-yellow-50 outline-none text-sm rounded px-1 ${className}`}
        dir="rtl"
        style={{ minWidth: type === "number" ? 60 : 80 }}
      />
    );
  }
  return (
    <span
      onDoubleClick={() => {
        setDraft(value);
        setEditing(true);
      }}
      title="انقر مرتين للتعديل"
      className={`cursor-pointer hover:bg-blue-50 hover:text-blue-700 rounded px-1 transition-colors ${className}`}
    >
      {value !== "" && value !== null && value !== undefined ? value : "—"}
    </span>
  );
}

const StatCard = ({ title, value, icon, badge, badgeColor }) => (
  <div className="bg-white p-6 rounded-[2rem] shadow-sm relative overflow-hidden">
    <div
      className={`absolute top-4 left-4 text-xs font-bold px-2 py-1 rounded-lg ${badgeColor}`}
    >
      {badge}
    </div>
    <div className="text-3xl mb-4">{icon}</div>
    <div className="text-right">
      <p className="text-gray-400 text-sm font-medium">{title}</p>
      <h3 className="text-3xl font-black text-slate-800 mt-1">{value}</h3>
    </div>
  </div>
);

function StatsCards({ activities }) {
  const totalPlanned = activities.reduce(
    (s, a) => s + (Number(a.planned) || 0),
    0,
  );
  const totalActual = activities.reduce(
    (s, a) => s + (Number(a.actual) || 0),
    0,
  );
  const pct =
    totalPlanned > 0 ? Math.round((totalActual / totalPlanned) * 100) : 0;

  const pctColor = pct >= 100 ? "#2E7D32" : pct >= 75 ? "#E65100" : "#C62828";
  const pctBadge = pct >= 100 ? "ممتاز" : pct >= 75 ? "جيد" : "منخفض";
  const pctBadgeColor =
    pct >= 100
      ? "text-green-600 bg-green-50"
      : pct >= 75
        ? "text-orange-500 bg-orange-50"
        : "text-red-500 bg-red-50";

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10 font-ibm">
      <StatCard
        title="إجمالي الأنشطة"
        value={activities.length}
        icon={<Target size={40} className="text-[#546E7A]" />}
        badge="نشط"
        badgeColor="text-blue-500 bg-blue-50"
      />
      <StatCard
        title="إجمالي المخطط"
        value={totalPlanned}
        icon={<BarChart2 size={40} className="text-[#546E7A]" />}
        badge="مخطط"
        badgeColor="text-blue-500 bg-blue-50"
      />
      <StatCard
        title="إجمالي المنجز"
        value={totalActual}
        icon={<CheckCircle size={40} className="text-[#546E7A]" />}
        badge="منجز"
        badgeColor="text-green-600 bg-green-50"
      />
      <StatCard
        title="نسبة الإنجاز"
        value={`${pct}%`}
        icon={<TrendingUp size={40} className="text-[#546E7A]" />}
        badge={pctBadge}
        badgeColor={pctBadgeColor}
      />
    </div>
  );
}

// ─── Modal ───────────────────────────────────────────────────────────────────
const UNIT_OPTIONS = [
  { value: "تقرير", label: "تقرير" },
  { value: "ممول / مسجل", label: "ممول / مسجل" },
  { value: "مأمورية", label: "مأمورية" },
  { value: "ندوة", label: "ندوة" },
  { value: "منشور / دليل", label: "منشور / دليل" },
  { value: "دراسة", label: "دراسة" },
  { value: "بيان", label: "بيان" },
  { value: "نموذج", label: "نموذج" },
  { value: "خطاب", label: "خطاب" },
];

function Modal({ mode, parentName, onClose, onSave, saving }) {
  const [form, setForm] = useState({
    name: "",
    unit: "",
    q: "",
    s: "",
    planned: "",
    actual: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = () => {
    if (!form.name.trim()) {
      alert("يرجى إدخال اسم النشاط");
      return;
    }
    onSave({
      ...form,
      planned: Number(form.planned) || 0,
      actual: Number(form.actual) || 0,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
        dir="rtl"
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-bold text-[#263238]">
              {mode === "activity" ? "إضافة نشاط جديد" : "إضافة نشاط فرعي"}
            </h3>
            {mode === "sub" && (
              <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">
                {parentName}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          <FloatingInput
            id="f-name"
            label="اسم النشاط"
            value={form.name}
            onChange={(v) => set("name", v)}
            required
          />
          <FloatingSelect
            id="f-unit"
            label="وحدة الأداء"
            value={form.unit}
            onChange={(v) => set("unit", v)}
            options={UNIT_OPTIONS}
          />

          <div className="grid grid-cols-2 gap-4">
            <FloatingInput
              id="f-q"
              label="معيار الأداء (ق)"
              type="number"
              value={form.q}
              onChange={(v) => set("q", v)}
            />
            <FloatingInput
              id="f-s"
              label="معيار الأداء (س)"
              type="number"
              value={form.s}
              onChange={(v) => set("s", v)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FloatingInput
              id="f-planned"
              label="حجم العمل المخطط"
              type="number"
              value={form.planned}
              onChange={(v) => set("planned", v)}
            />
            <FloatingInput
              id="f-actual"
              label="حجم العمل المنجز"
              type="number"
              value={form.actual}
              onChange={(v) => set("actual", v)}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end px-6 pb-5">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            إلغاء
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 rounded-xl bg-[#546E7A] hover:bg-[#263238] text-white text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-60"
          >
            <Plus size={15} /> {saving ? "جاري الحفظ..." : "حفظ النشاط"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Export Excel ─────────────────────────────────────────────────────────────
// ألوان وحدود متطابقة مع شيت الإكسل الأصلي
const XL_YELLOW = "FFFF00";
const XL_GREEN = "C6E0B4";
const XL_ORANGE = "FCE4D6";
const XL_BLUE = "DDEBF7";
const XL_THIN_BORDER = { style: "thin", color: { argb: "FFB0B0B0" } };
const xlBorderAll = {
  top: XL_THIN_BORDER,
  bottom: XL_THIN_BORDER,
  left: XL_THIN_BORDER,
  right: XL_THIN_BORDER,
};

function xlFill(hex) {
  return { type: "pattern", pattern: "solid", fgColor: { argb: hex } };
}

function styleRow(ws, rowIdx, { fill, bold, color, size } = {}) {
  const row = ws.getRow(rowIdx);
  row.eachCell({ includeEmpty: true }, (cell) => {
    cell.border = xlBorderAll;
    cell.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
    if (fill) cell.fill = xlFill(fill);
    cell.font = {
      name: "Arial",
      bold: !!bold,
      size: size || 11,
      color: color ? { argb: color } : undefined,
    };
  });
}

async function exportToExcel(activities) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("الخطة الشهرية", {
    views: [{ rightToLeft: true }],
  });

  ws.columns = [
    { width: 55 }, // الأنشطة
    { width: 16 }, // وحدة الأداء
    { width: 8 }, // ق
    { width: 8 }, // س
    { width: 10 }, // المخطط
    { width: 10 }, // المنجز
  ];

  // ── رأس الشيت ──
  ws.addRow([
    "الإدارة العامة للعمليات والتوقيع الإلكتروني",
    "",
    "",
    "",
    "أبريل ٢٠٢٦",
    "19",
  ]);
  ws.mergeCells("A1:D1");
  styleRow(ws, 1, { fill: XL_YELLOW, bold: true, size: 13 });

  ws.addRow(["العدد", "", "مدير إدارات", "مليون إدارات", "باحث", "داري"]);
  ws.mergeCells("A2:B2");
  styleRow(ws, 2, { fill: XL_GREEN, bold: true });

  ws.addRow(["القوى العاملة", "", "", "", "4", "13"]);
  ws.mergeCells("A3:D3");
  styleRow(ws, 3, { fill: XL_GREEN, bold: true });

  ws.addRow(["أيام التدريب - أيام المرضى", "", "", "", "", ""]);
  ws.mergeCells("A4:F4");
  styleRow(ws, 4, { fill: XL_GREEN, bold: true });

  ws.addRow(["أيام الإجازات الرسمية", "1", "", "", "", ""]);
  ws.mergeCells("A5:F5");
  styleRow(ws, 5, { fill: XL_GREEN, bold: true });

  ws.addRow(["الأنشطة", "وحدة الأداء", "معيار الأداء", "", "حجم العمل", ""]);
  ws.mergeCells("C6:D6");
  ws.mergeCells("E6:F6");
  styleRow(ws, 6, { fill: XL_YELLOW, bold: true });

  ws.addRow(["", "", "ق", "س", "المخطط", "المنجز"]);
  styleRow(ws, 7, { fill: XL_YELLOW, bold: true });

  // ── الأنشطة ──
  activities.forEach((act) => {
    const r = ws.addRow([
      act.name,
      act.unit,
      act.q || "",
      act.s || "",
      act.planned,
      act.actual,
    ]);
    styleRow(ws, r.number, { fill: XL_ORANGE, bold: true });
    ws.getRow(r.number).getCell(6).font = {
      name: "Arial",
      bold: true,
      color: { argb: "FF2E7D32" },
    };
    ws.getRow(r.number).getCell(1).alignment = {
      horizontal: "right",
      vertical: "middle",
      wrapText: true,
      indent: 1,
    };

    act.subTasks.forEach((sub) => {
      const sr = ws.addRow([
        "—  " + sub.name,
        sub.unit || act.unit,
        sub.q || "",
        sub.s || "",
        sub.planned,
        sub.actual,
      ]);
      styleRow(ws, sr.number, { fill: XL_BLUE });
      ws.getRow(sr.number).getCell(6).font = {
        name: "Arial",
        color: { argb: "FF2E7D32" },
      };
      ws.getRow(sr.number).getCell(1).alignment = {
        horizontal: "right",
        vertical: "middle",
        wrapText: true,
        indent: 2,
      };
    });
  });

  // ── شيتات فاضية للخطة الربع سنوية والسنوية والاستراتيجية ──
  const wsQ = wb.addWorksheet("الخطة الربع سنوية", {
    views: [{ rightToLeft: true }],
  });
  wsQ.addRow(["الخطة الربع سنوية - قيد الإعداد"]);
  const wsA = wb.addWorksheet("الخطة السنوية", {
    views: [{ rightToLeft: true }],
  });
  wsA.addRow(["الخطة السنوية - قيد الإعداد"]);
  const wsS = wb.addWorksheet("الخطة الاستراتيجية", {
    views: [{ rightToLeft: true }],
  });
  wsS.addRow(["الخطة الاستراتيجية - قيد الإعداد"]);

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "خطة_الإدارة_العامة_للعمليات.xlsx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Main Grid ────────────────────────────────────────────────────────────────
function PlanGrid({ activities, onUpdateCell, onDelete }) {
  const [expandedRows, setExpandedRows] = useState({});

  // فتح كل الصفوف تلقائياً عند أول تحميل للداتا
  useEffect(() => {
    const initial = {};
    activities.forEach((a) => (initial[a.id] = true));
    setExpandedRows(initial);
  }, [activities.length]);

  const toggleRow = (id) => setExpandedRows((p) => ({ ...p, [id]: !p[id] }));

  const colClass = "grid-cols-[40px_1fr_120px_56px_56px_90px_90px_96px]";
  const thBase =
    "p-3 text-xs font-semibold text-[#546E7A] text-center border-l border-gray-100 last:border-l-0 bg-[#F8F9FA] select-none";

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100">
      <div className="min-w-[860px]">
        {/* Double header */}
        <div className={`grid ${colClass}`}>
          <div
            className={`${thBase} row-span-2 flex items-center justify-center border-b border-gray-200`}
          ></div>
          <div className={`${thBase} text-right px-4 border-b border-gray-200`}>
            الأنشطة
          </div>
          <div className={`${thBase} border-b border-gray-200`}>
            وحدة الأداء
          </div>
          <div
            className={`${thBase} col-span-2 border-b-0 border-gray-200 text-center`}
          >
            معيار الأداء
          </div>
          <div
            className={`${thBase} col-span-2 border-l-0 border-b-0 border-gray-200 text-center`}
          >
            حجم العمل
          </div>
          <div
            className={`${thBase} row-span-2 flex items-center justify-center border-b border-gray-200`}
          >
            إجراءات
          </div>
        </div>
        <div className={`grid ${colClass} border-b border-gray-200`}>
          <div></div>
          <div className={`${thBase} border-t-0 text-right px-4`}></div>
          <div></div>
          <div className={`${thBase} border-t border-gray-100`}>ق</div>
          <div className={`${thBase} border-t border-gray-100`}>س</div>
          <div className={`${thBase} border-t border-gray-100`}>المخطط</div>
          <div className={`${thBase} border-t border-gray-100 border-l-0`}>
            المنجز
          </div>
        </div>

        {/* Rows */}
        {activities.map((row) => (
          <React.Fragment key={row.id}>
            {/* Main row */}
            <div
              className={`grid ${colClass} border-b border-gray-50 hover:bg-gray-50/60 transition-colors group`}
            >
              <div className="flex items-center justify-center p-2">
                <button
                  onClick={() => toggleRow(row.id)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-[#546E7A] hover:bg-[#546E7A]/10 transition-colors"
                >
                  {expandedRows[row.id] ? (
                    <ChevronUp size={15} />
                  ) : (
                    <ChevronDown size={15} />
                  )}
                </button>
              </div>
              <div className="p-3 flex items-center pr-4">
                <EditableCell
                  value={row.name}
                  onSave={(v) => onUpdateCell(row.id, "name", v)}
                  className="font-semibold text-[#263238] text-lg leading-snug"
                />
              </div>
              <div className="p-3 flex items-center justify-center">
                <EditableCell
                  value={row.unit}
                  onSave={(v) => onUpdateCell(row.id, "unit", v)}
                  className="text-gray-500 text-sm"
                />
              </div>
              <div className="p-3 flex items-center justify-center">
                <EditableCell
                  value={row.q ?? ""}
                  onSave={(v) => onUpdateCell(row.id, "criteriaQ", v)}
                  type="number"
                  className="text-gray-600 text-sm"
                />
              </div>
              <div className="p-3 flex items-center justify-center">
                <EditableCell
                  value={row.s ?? ""}
                  onSave={(v) => onUpdateCell(row.id, "criteriaS", v)}
                  type="number"
                  className="text-gray-600 text-sm"
                />
              </div>
              <div className="p-3 flex items-center justify-center">
                <EditableCell
                  value={row.planned}
                  onSave={(v) => onUpdateCell(row.id, "plannedVolume", v)}
                  type="number"
                  className="font-bold text-[#263238] text-sm"
                />
              </div>
              <div className="p-3 flex items-center justify-center">
                <EditableCell
                  value={row.actual}
                  onSave={(v) => onUpdateCell(row.id, "actualVolume", v)}
                  type="number"
                  className="font-bold text-green-600 text-sm"
                />
              </div>
              <div className="p-3 flex items-center justify-center gap-1">
                <button
                  onClick={() => {
                    document.dispatchEvent(
                      new CustomEvent("openSubModal", {
                        detail: { parentId: row.id },
                      }),
                    );
                  }}
                  title="إضافة نشاط فرعي"
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-[#546E7A] hover:bg-[#546E7A]/10 transition-colors"
                >
                  <Plus size={14} />
                </button>
                <button
                  onClick={() => onDelete(row.id)}
                  title="حذف النشاط"
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            {/* Sub rows */}
            {expandedRows[row.id] &&
              row.subTasks.map((sub) => (
                <div
                  key={sub.id}
                  className={`grid ${colClass} border-b border-gray-50 bg-[#FAFBFC] hover:bg-blue-50/30 transition-colors group`}
                >
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-b-2 border-r-2 border-gray-200 rounded-br-sm mr-2 mt-[-8px]" />
                  </div>
                  <div className="p-2.5 flex items-start pr-8 gap-1">
                    <span className="text-gray-300 text-xs mt-0.5 flex-shrink-0">
                      ——
                    </span>
                    <EditableCell
                      value={sub.name}
                      onSave={(v) => onUpdateCell(sub.id, "name", v)}
                      className="text-gray-600 text-xs leading-snug"
                    />
                  </div>
                  <div className="p-2.5 flex items-center justify-center">
                    <EditableCell
                      value={sub.unit || row.unit}
                      onSave={(v) => onUpdateCell(sub.id, "unit", v)}
                      className="text-gray-400 text-xs"
                    />
                  </div>
                  <div className="p-2.5 flex items-center justify-center">
                    <EditableCell
                      value={sub.q ?? ""}
                      onSave={(v) => onUpdateCell(sub.id, "criteriaQ", v)}
                      type="number"
                      className="text-gray-500 text-xs"
                    />
                  </div>
                  <div className="p-2.5 flex items-center justify-center">
                    <EditableCell
                      value={sub.s ?? ""}
                      onSave={(v) => onUpdateCell(sub.id, "criteriaS", v)}
                      type="number"
                      className="text-gray-500 text-xs"
                    />
                  </div>
                  <div className="p-2.5 flex items-center justify-center">
                    <EditableCell
                      value={sub.planned}
                      onSave={(v) => onUpdateCell(sub.id, "plannedVolume", v)}
                      type="number"
                      className="font-medium text-gray-700 text-xs"
                    />
                  </div>
                  <div className="p-2.5 flex items-center justify-center">
                    <EditableCell
                      value={sub.actual}
                      onSave={(v) => onUpdateCell(sub.id, "actualVolume", v)}
                      type="number"
                      className="font-medium text-green-600 text-xs"
                    />
                  </div>
                  <div className="p-2.5 flex items-center justify-center">
                    <button
                      onClick={() => onDelete(sub.id)}
                      className="w-6 h-6 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
          </React.Fragment>
        ))}

        {/* Empty state */}
        {activities.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-300">
            <BarChart2 size={40} />
            <p className="mt-3 text-sm">
              لا توجد أنشطة بعد — أضف نشاطاً جديداً
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab Content ──────────────────────────────────────────────────────────────
function EmptyTab({ label }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-gray-300">
      <Calendar size={44} />
      <p className="mt-4 text-base font-medium text-gray-400">{label}</p>
      <p className="text-sm text-gray-300 mt-1">ستتوفر هذه الخطة قريباً</p>
    </div>
  );
}

// ─── ROOT COMPONENT ───────────────────────────────────────────────────────────
export default function PlanAdmin() {
  useEffect(() => {
    document.title = "خطة - الإدارة العامة للعمليات والتوقيع الإلكتروني";
  }, []);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("monthly");

  const [periods, setPeriods] = useState([]);
  const [currentPeriodId, setCurrentPeriodId] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [modal, setModal] = useState(null); // null | { mode: 'activity' | 'sub', parentId? }
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [duplicateError, setDuplicateError] = useState(false);
  const [periodForm, setPeriodForm] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });

  const [newPeriod, setNewPeriod] = useState({
    month: "",
    year: new Date().getFullYear(),
    // ... أي حقول أخرى
  });

  // ── جلب الفترات أول ما تفتح الصفحة ──
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await PlanAPI.getPeriods();
        setPeriods(data);
        if (data.length > 0) setCurrentPeriodId(data[0].id);
        else setLoading(false); // مفيش فترات بعد
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    })();
  }, []);

  // ── جلب الأنشطة لما تتغير الفترة الحالية ──
  const loadActivities = useCallback(async (periodId) => {
    if (!periodId) return;
    try {
      setLoading(true);
      const flat = await PlanAPI.getActivities(periodId);
      setActivities(buildActivityTree(flat));
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // هذا الـ useEffect يقوم بمهمتين:
  // 1. مراقبة تغيير الشهر والسنة والبحث عن الفترة المقابلة لهما
  // 2. تحديث الـ currentPeriodId تلقائياً ليقوم الـ useEffect التالي بجلب البيانات
  useEffect(() => {
    if (periodForm.month && periodForm.year) {
      const foundPeriod = periods.find(
        (p) =>
          p.periodMonth === periodForm.month &&
          p.periodYear === periodForm.year,
      );

      if (foundPeriod) {
        setCurrentPeriodId(foundPeriod.id);
        setDuplicateError(false);
      } else {
        setCurrentPeriodId(null);
      }
    }
  }, [periodForm.month, periodForm.year, periods]);

  // هذا الـ useEffect الخاص بجلب الأنشطة يظل كما هو
  // سيعمل تلقائياً بمجرد أن يقوم الـ useEffect أعلاه بتغيير currentPeriodId
  useEffect(() => {
    if (currentPeriodId) loadActivities(currentPeriodId);
  }, [currentPeriodId, loadActivities]);

  // ── إنشاء فترة جديدة بشهر وسنة مختارين ──
  const createDefaultPeriod = () => {
    setPeriodForm({
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
    });
    setDuplicateError(false);
    setShowPeriodModal(true);
  };

  const createPeriodFromForm = async () => {
    // التحقق من عدم تكرار نفس الشهر والسنة
    const alreadyExists = periods.some(
      (p) =>
        p.periodMonth === periodForm.month && p.periodYear === periodForm.year,
    );
    if (alreadyExists) {
      setDuplicateError(true);
      return;
    }
    setDuplicateError(false);
    try {
      setSaving(true);
      const date = new Date(periodForm.year, periodForm.month - 1, 1);
      const label = date.toLocaleDateString("ar-EG", {
        month: "long",
        year: "numeric",
      });
      const res = await PlanAPI.createPeriod({
        periodType: "monthly",
        periodLabel: label,
        periodYear: periodForm.year,
        periodMonth: periodForm.month,
      });
      const newPeriod = {
        id: res.id,
        periodLabel: label,
        periodMonth: periodForm.month,
        periodYear: periodForm.year,
      };
      setPeriods((p) => [newPeriod, ...p]);
      setCurrentPeriodId(res.id);
      setShowPeriodModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Listen for sub-modal open from grid
  useEffect(() => {
    const handler = (e) =>
      setModal({ mode: "sub", parentId: e.detail.parentId });
    document.addEventListener("openSubModal", handler);
    return () => document.removeEventListener("openSubModal", handler);
  }, []);

  const saveModal = useCallback(
    async (form) => {
      if (!currentPeriodId) return;
      try {
        setSaving(true);
        await PlanAPI.createActivity({
          periodId: currentPeriodId,
          parentActivityId: modal.mode === "sub" ? modal.parentId : null,
          name: form.name,
          unit: form.unit,
          criteriaQ: form.q === "" ? null : Number(form.q),
          criteriaS: form.s === "" ? null : Number(form.s),
          plannedVolume: form.planned,
          actualVolume: form.actual,
        });
        await loadActivities(currentPeriodId);
        setModal(null);
      } catch (err) {
        alert(err.message);
      } finally {
        setSaving(false);
      }
    },
    [modal, currentPeriodId, loadActivities],
  );

  // تعديل خلية واحدة (نقرتين) — تحديث تفاؤلي + مزامنة مع السيرفر
  const handleUpdateCell = useCallback(
    async (id, columnName, value) => {
      setActivities((prev) =>
        prev.map((a) => {
          if (a.id === id) return { ...a, [mapField(columnName)]: value };
          return {
            ...a,
            subTasks: a.subTasks.map((s) =>
              s.id === id ? { ...s, [mapField(columnName)]: value } : s,
            ),
          };
        }),
      );
      try {
        await PlanAPI.updateActivityCell(id, columnName, value);
      } catch (err) {
        alert("فشل حفظ التعديل: " + err.message);
        loadActivities(currentPeriodId);
      }
    },
    [currentPeriodId, loadActivities],
  );

  const handleDelete = useCallback(
    async (id) => {
      if (!window.confirm("هل تريد حذف هذا النشاط؟")) return;
      try {
        await PlanAPI.deleteActivity(id);
        await loadActivities(currentPeriodId);
      } catch (err) {
        alert("فشل الحذف: " + err.message);
      }
    },
    [currentPeriodId, loadActivities],
  );

  const TABS = [
    { key: "monthly", label: "الخطة الشهرية" },
    { key: "quarterly", label: "الخطة الربع سنوية" },
    { key: "annual", label: "الخطة السنوية" },
    { key: "strategic", label: "الخطة الاستراتيجية" },
  ];

  const parentActivity =
    modal?.mode === "sub"
      ? activities.find((a) => a.id === modal.parentId)
      : null;

  return (
    <div className="flex min-h-screen bg-[#F4F6F8]" dir="rtl">
      <Sidebar
        isOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen((p) => !p)}
      />

      <div
        className={`flex-1 min-w-0 transition-all duration-300 p-6 md:p-8 ${isSidebarOpen ? "lg:mr-64" : "lg:mr-20"}`}
      >
        <Header
          isSidebarOpen={isSidebarOpen}
          setSidebarOpen={setIsSidebarOpen}
          title="الخطة"
        />

        {/* Page title */}
        <div className="mb-6 text-right flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl md:text-3xl font-ibm font-extrabold text-[#546E7A]">
              خطة الإدارة
            </h2>
            <p className="text-gray-400 text-sm mt-1">
              الخطة الشهرية والربع سنوية والسنوية والاستراتيجية للإدارة العامة للعمليات
            </p>
          </div>

          {/* اختيار الفترة */}
          <div className="flex items-center gap-2">
            {/* {periods.length > 0 && (
              <select
                dir="rtl"
                value={currentPeriodId || ""}
                onChange={(e) => setCurrentPeriodId(Number(e.target.value))}
                className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white outline-none appearance-none"
              >
                {periods.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.periodLabel || `فترة #${p.id}`}
                  </option>
                ))}
              </select>
              
            )} */}
            {/* اختيار الشهر */}
            <div className="relative flex-1">
              <select
                value={periodForm.month || ""}
                onChange={(e) =>
                  setPeriodForm({
                    ...periodForm,
                    month: parseInt(e.target.value),
                  })
                }
                className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white outline-none appearance-none"
              >
                <option value="" disabled>
                  الشهر
                </option>
                {[
                  "يناير",
                  "فبراير",
                  "مارس",
                  "أبريل",
                  "مايو",
                  "يونيو",
                  "يوليو",
                  "أغسطس",
                  "سبتمبر",
                  "أكتوبر",
                  "نوفمبر",
                  "ديسمبر",
                ].map((m, i) => (
                  <option key={i} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            {/* اختيار السنة */}
            <div className="relative flex-1">
              <select
                value={periodForm.year || ""}
                onChange={(e) =>
                  setPeriodForm({
                    ...periodForm,
                    year: parseInt(e.target.value),
                  })
                }
                className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white outline-none appearance-none"
              >
                <option value="" disabled>
                  السنة
                </option>
                {[2024, 2025, 2026, 2027].map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={createDefaultPeriod}
              className="bg-[#546E7A] hover:bg-[#263238] text-white font-ibm px-8 py-3 rounded-2xl font-bold flex items-center gap-2 transition shadow-lg shadow-[#546e7a20] w-full md:w-auto justify-center active:scale-95"
            >
              + اضافه خطه جديد
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-600 text-sm text-right">
            {error}
          </div>
        )}

        {/* لا توجد فترات بعد */}
        {!loading && periods.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-10 text-center">
            <p className="text-gray-400 mb-4">لا توجد فترة خطة بعد</p>
            <button
              onClick={createDefaultPeriod}
              disabled={saving}
              className="bg-[#546E7A] hover:bg-[#263238] text-white font-ibm px-6 py-2.5 rounded-xl font-bold transition disabled:opacity-60"
            >
              {saving ? "جاري الإنشاء..." : "إنشاء فترة جديدة"}
            </button>
          </div>
        )}

        {/* فيه فترات بالفعل، لكن مفيش خطة للشهر/السنة المختارة حاليًا */}
        {!loading && periods.length > 0 && !currentPeriodId && (
          <div className="bg-white rounded-2xl shadow-sm p-10 text-center">
            <p className="text-gray-400 mb-1">
              لا توجد خطة لهذا الشهر والسنة
            </p>
            <p className="text-sm text-gray-300 mb-4">
              اختر شهرًا وسنة آخرين، أو أضف خطة جديدة لهذه الفترة
            </p>
            <button
              onClick={() => {
                setDuplicateError(false);
                setShowPeriodModal(true);
              }}
              disabled={saving}
              className="bg-[#546E7A] hover:bg-[#263238] text-white font-ibm px-6 py-2.5 rounded-xl font-bold transition disabled:opacity-60"
            >
              {saving ? "جاري الإنشاء..." : "+ إضافة خطة لهذه الفترة"}
            </button>
          </div>
        )}

        {periods.length > 0 && currentPeriodId && (
          <>
            {/* Stats */}
            <StatsCards activities={activities} />

            {/* Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Tabs + actions bar */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-6 pt-5 pb-0 border-b border-gray-100">
                {/* Tabs */}
                <div className="flex gap-0">
                  {TABS.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setActiveTab(t.key)}
                      className={`px-5 py-3 text-sm font-medium transition-all border-b-2 ${
                        activeTab === t.key
                          ? "text-[#546E7A] border-[#546E7A]"
                          : "text-gray-400 border-transparent hover:text-gray-600"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                {/* Action buttons */}
                <div className="flex gap-2 pb-4">
                  <button
                    onClick={() => setModal({ mode: "activity" })}
                    className="bg-[#546E7A] hover:bg-[#263238] text-white font-ibm px-8 py-3 rounded-2xl font-bold flex items-center gap-2 transition shadow-lg shadow-[#546e7a20] w-full md:w-auto justify-center active:scale-95"
                  >
                    <Plus size={20} /> إضافة نشاط
                  </button>
                  <button
                    onClick={() => exportToExcel(activities)}
                    className="flex items-center gap-2 px-5 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-bold transition shadow-md"
                  >
                    <FileSpreadsheet size={16} /> تصدير Excel
                  </button>
                </div>
              </div>

              {/* Tab content */}
              <div className="p-4 md:p-6">
                {loading && (
                  <div className="py-16 text-center text-gray-400 text-sm">
                    جاري تحميل البيانات...
                  </div>
                )}
                {!loading && activeTab === "monthly" && (
                  <PlanGrid
                    activities={activities}
                    onUpdateCell={handleUpdateCell}
                    onDelete={handleDelete}
                  />
                )}
                {!loading && activeTab === "quarterly" && (
                  <EmptyTab label="الخطة الربع سنوية" />
                )}
                {!loading && activeTab === "annual" && (
                  <EmptyTab label="الخطة السنوية" />
                )}
                {!loading && activeTab === "strategic" && (
                  <EmptyTab label="الخطة الاستراتيجية" />
                )}
              </div>

              {/* Hint */}
              {activeTab === "monthly" && !loading && (
                <div className="px-6 pb-4 text-xs text-gray-300 text-right">
                  * انقر مرتين على أي خلية لتعديلها مباشرة
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <Modal
          mode={modal.mode}
          parentName={parentActivity?.name}
          onClose={() => setModal(null)}
          onSave={saveModal}
          saving={saving}
        />
      )}

      {/* Modal اختيار الشهر والسنة */}
      {showPeriodModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-[#263238]/40 backdrop-blur-sm"
            onClick={() => setShowPeriodModal(false)}
          />
          <div
            className="relative bg-white w-full max-w-sm rounded-[2rem] shadow-2xl p-8 animate-in zoom-in duration-200"
            dir="rtl"
          >
            <h3 className="text-lg font-bold text-[#263238] mb-6 text-right">
              إنشاء خطة شهرية جديدة
            </h3>

            <div className="space-y-4">
              {/* الشهر */}
              <div className="relative">
                <select
                  value={periodForm.month}
                  onChange={(e) =>
                    setPeriodForm({
                      ...periodForm,
                      month: Number(e.target.value),
                    })
                  }
                  className="peer w-full px-5 py-4 rounded-2xl border border-gray-100 bg-gray-50 focus:bg-white focus:border-[#546E7A] outline-none transition-all pt-7 text-right appearance-none"
                >
                  {[
                    { v: 1, l: "يناير" },
                    { v: 2, l: "فبراير" },
                    { v: 3, l: "مارس" },
                    { v: 4, l: "أبريل" },
                    { v: 5, l: "مايو" },
                    { v: 6, l: "يونيو" },
                    { v: 7, l: "يوليو" },
                    { v: 8, l: "أغسطس" },
                    { v: 9, l: "سبتمبر" },
                    { v: 10, l: "أكتوبر" },
                    { v: 11, l: "نوفمبر" },
                    { v: 12, l: "ديسمبر" },
                  ].map((m) => {
                    const exists = periods.some(
                      (p) =>
                        p.periodMonth === m.v &&
                        p.periodYear === periodForm.year,
                    );
                    return (
                      <option key={m.v} value={m.v} disabled={exists}>
                        {m.l}
                        {exists ? " ✓ موجود" : ""}
                      </option>
                    );
                  })}
                </select>
                <label
                  htmlFor="roleId"
                  className="absolute text-sm font-bold text-gray-400 duration-300 transform -translate-y-0 scale-75 top-4 z-10 origin-[100%] right-5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3 peer-focus:text-[#546E7A]"
                >
                  الشهر
                </label>
                <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 peer-focus:text-[#546E7A]">
                  <ChevronDown size={18} />
                </div>
              </div>

              {/* السنة */}
              <div className="relative">
                <select
                  value={periodForm.year}
                  onChange={(e) =>
                    setPeriodForm({
                      ...periodForm,
                      year: Number(e.target.value),
                    })
                  }
                  className="peer w-full px-5 py-4 rounded-2xl border border-gray-100 bg-gray-50 focus:bg-white focus:border-[#546E7A] outline-none transition-all pt-7 text-right appearance-none"
                >
                  {[2023, 2024, 2025, 2026, 2027, 2028].map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                <label
                  htmlFor="roleId"
                  className="absolute text-sm font-bold text-gray-400 duration-300 transform -translate-y-0 scale-75 top-4 z-10 origin-[100%] right-5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3 peer-focus:text-[#546E7A]"
                >
                  السنه
                </label>
                <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 peer-focus:text-[#546E7A]">
                  <ChevronDown size={18} />
                </div>
              </div>
            </div>

            {duplicateError && (
              <p className="text-red-500 text-sm text-right mt-3 font-bold">
                ⚠️ هذا الشهر موجود بالفعل، اختر شهراً مختلفاً
              </p>
            )}

            <div className="flex gap-3 mt-4">
              <button
                onClick={createPeriodFromForm}
                disabled={saving}
                className="flex-1 bg-[#546E7A] hover:bg-[#263238] text-white font-bold py-3 rounded-xl transition disabled:opacity-60"
              >
                {saving ? "جاري الإنشاء..." : "إنشاء الخطة"}
              </button>
              <button
                onClick={() => setShowPeriodModal(false)}
                className="flex-1 bg-gray-100 text-gray-500 font-bold py-3 rounded-xl"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// تحويل اسم العمود القادم من الـ API لاسم الحقل في حالة الواجهة (q/s/planned/actual)
function mapField(columnName) {
  switch (columnName) {
    case "criteriaQ":
      return "q";
    case "criteriaS":
      return "s";
    case "plannedVolume":
      return "planned";
    case "actualVolume":
      return "actual";
    default:
      return columnName; // name, unit
  }
}